/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NumberInput, ResultBox } from './parts';

// ---------------- Braden Scale (rischio ulcere da decubito) ----------------
const BRADEN_ITEMS = [
  { key: 'perception', label: 'Percezione sensoriale',
    options: [
      { v: 1, l: 'Completamente limitata' }, { v: 2, l: 'Molto limitata' },
      { v: 3, l: 'Lievemente limitata' }, { v: 4, l: 'Nessuna alterazione' },
    ] },
  { key: 'moisture', label: 'Umidità della cute',
    options: [
      { v: 1, l: 'Costantemente umida' }, { v: 2, l: 'Molto umida' },
      { v: 3, l: 'Occasionalmente umida' }, { v: 4, l: 'Raramente umida' },
    ] },
  { key: 'activity', label: 'Attività',
    options: [
      { v: 1, l: 'Allettato' }, { v: 2, l: 'In poltrona' },
      { v: 3, l: 'Cammina occasionalmente' }, { v: 4, l: 'Cammina frequentemente' },
    ] },
  { key: 'mobility', label: 'Mobilità',
    options: [
      { v: 1, l: 'Completamente immobile' }, { v: 2, l: 'Molto limitata' },
      { v: 3, l: 'Lievemente limitata' }, { v: 4, l: 'Nessuna limitazione' },
    ] },
  { key: 'nutrition', label: 'Nutrizione',
    options: [
      { v: 1, l: 'Molto scarsa' }, { v: 2, l: 'Probabilmente inadeguata' },
      { v: 3, l: 'Adeguata' }, { v: 4, l: 'Eccellente' },
    ] },
  { key: 'friction', label: 'Frizione e scivolamento',
    options: [
      { v: 1, l: 'Problema' }, { v: 2, l: 'Problema potenziale' },
      { v: 3, l: 'Nessun problema apparente' },
    ] },
];
export const BradenCalc = () => {
  const [state, setState] = useState({});
  const set = (k, v) => setState((s) => ({ ...s, [k]: Number(v) }));
  const complete = BRADEN_ITEMS.every((i) => state[i.key]);
  const score = complete ? BRADEN_ITEMS.reduce((sum, i) => sum + (state[i.key] || 0), 0) : null;
  let tone = 'neutral';
  let interp = null;
  if (score !== null) {
    if (score <= 9) { tone = 'danger'; interp = 'Rischio estremamente elevato'; }
    else if (score <= 12) { tone = 'danger'; interp = 'Rischio molto elevato'; }
    else if (score <= 14) { tone = 'warning'; interp = 'Rischio moderato'; }
    else if (score <= 18) { tone = 'warning'; interp = 'Rischio lieve'; }
    else { tone = 'good'; interp = 'Nessun rischio significativo'; }
  }
  return (
    <div className="space-y-3">
      <div className="grid gap-3">
        {BRADEN_ITEMS.map((item) => (
          <div key={item.key} className="space-y-1.5">
            <Label className="text-xs">{item.label}</Label>
            <Select value={state[item.key]?.toString() || ''} onValueChange={(v) => set(item.key, v)}>
              <SelectTrigger data-testid={`calc-input-braden-${item.key}`}>
                <SelectValue placeholder="Seleziona…" />
              </SelectTrigger>
              <SelectContent>
                {item.options.map((o) => (
                  <SelectItem key={o.v} value={String(o.v)}>{o.v} · {o.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
      <ResultBox value={score} unit={score ? '/ 23' : ''} interpretation={interp} tone={tone} />
    </div>
  );
};

// ---------------- Barthel Index (autonomia funzionale) ----------------
const BARTHEL_ITEMS = [
  { key: 'feeding', label: 'Alimentazione', options: [
    { v: 0, l: 'Dipendente' }, { v: 5, l: 'Necessita aiuto' }, { v: 10, l: 'Autonomo' },
  ]},
  { key: 'bathing', label: 'Bagno', options: [
    { v: 0, l: 'Dipendente' }, { v: 5, l: 'Autonomo' },
  ]},
  { key: 'grooming', label: 'Igiene personale', options: [
    { v: 0, l: 'Necessita aiuto' }, { v: 5, l: 'Autonomo' },
  ]},
  { key: 'dressing', label: 'Vestirsi', options: [
    { v: 0, l: 'Dipendente' }, { v: 5, l: 'Necessita aiuto' }, { v: 10, l: 'Autonomo' },
  ]},
  { key: 'bowels', label: 'Continenza intestinale', options: [
    { v: 0, l: 'Incontinente' }, { v: 5, l: 'Episodi occasionali' }, { v: 10, l: 'Continente' },
  ]},
  { key: 'bladder', label: 'Continenza urinaria', options: [
    { v: 0, l: 'Incontinente' }, { v: 5, l: 'Episodi occasionali' }, { v: 10, l: 'Continente' },
  ]},
  { key: 'toilet', label: 'Uso del WC', options: [
    { v: 0, l: 'Dipendente' }, { v: 5, l: 'Necessita aiuto' }, { v: 10, l: 'Autonomo' },
  ]},
  { key: 'transfers', label: 'Trasferimento letto-poltrona', options: [
    { v: 0, l: 'Impossibile' }, { v: 5, l: 'Con 1-2 persone' },
    { v: 10, l: 'Supervisione minima' }, { v: 15, l: 'Autonomo' },
  ]},
  { key: 'mobility', label: 'Deambulazione', options: [
    { v: 0, l: 'Immobile' }, { v: 5, l: 'In carrozzina' },
    { v: 10, l: 'Con aiuto' }, { v: 15, l: '50m autonomo' },
  ]},
  { key: 'stairs', label: 'Salire scale', options: [
    { v: 0, l: 'Impossibile' }, { v: 5, l: 'Con aiuto' }, { v: 10, l: 'Autonomo' },
  ]},
];
export const BarthelCalc = () => {
  const [state, setState] = useState({});
  const set = (k, v) => setState((s) => ({ ...s, [k]: Number(v) }));
  const complete = BARTHEL_ITEMS.every((i) => state[i.key] !== undefined);
  const score = complete ? BARTHEL_ITEMS.reduce((sum, i) => sum + (state[i.key] || 0), 0) : null;
  let tone = 'neutral';
  let interp = null;
  if (score !== null) {
    if (score <= 20) { tone = 'danger'; interp = 'Dipendenza totale'; }
    else if (score <= 40) { tone = 'danger'; interp = 'Dipendenza grave'; }
    else if (score <= 60) { tone = 'warning'; interp = 'Dipendenza moderata'; }
    else if (score <= 90) { tone = 'warning'; interp = 'Dipendenza lieve'; }
    else if (score <= 99) { tone = 'good'; interp = 'Quasi indipendente'; }
    else { tone = 'good'; interp = 'Indipendenza completa'; }
  }
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        {BARTHEL_ITEMS.map((item) => (
          <div key={item.key} className="space-y-1.5">
            <Label className="text-xs">{item.label}</Label>
            <Select value={state[item.key]?.toString() || ''} onValueChange={(v) => set(item.key, v)}>
              <SelectTrigger data-testid={`calc-input-barthel-${item.key}`}>
                <SelectValue placeholder="Seleziona…" />
              </SelectTrigger>
              <SelectContent>
                {item.options.map((o) => (
                  <SelectItem key={o.v} value={String(o.v)}>{o.v} · {o.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
      <ResultBox value={score} unit={score !== null ? '/ 100' : ''} interpretation={interp} tone={tone} />
    </div>
  );
};

// ---------------- NEWS2 (Early Warning) ----------------
const NEWS2_SCORING = {
  rr:   (v) => v == null ? null : v <= 8 ? 3 : v <= 11 ? 1 : v <= 20 ? 0 : v <= 24 ? 2 : 3,
  spo2: (v) => v == null ? null : v <= 91 ? 3 : v <= 93 ? 2 : v <= 95 ? 1 : 0,
  o2:   (v) => v === 'yes' ? 2 : v === 'no' ? 0 : null,
  sbp:  (v) => v == null ? null : v <= 90 ? 3 : v <= 100 ? 2 : v <= 110 ? 1 : v <= 219 ? 0 : 3,
  hr:   (v) => v == null ? null : v <= 40 ? 3 : v <= 50 ? 1 : v <= 90 ? 0 : v <= 110 ? 1 : v <= 130 ? 2 : 3,
  avpu: (v) => v === 'A' ? 0 : (v ? 3 : null),
  temp: (v) => v == null ? null : v <= 35.0 ? 3 : v <= 36.0 ? 1 : v <= 38.0 ? 0 : v <= 39.0 ? 1 : 2,
};
export const News2Calc = () => {
  const [v, setV] = useState({ rr: null, spo2: null, o2: 'no', sbp: null, hr: null, avpu: 'A', temp: null });
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const perParam = Object.fromEntries(Object.entries(NEWS2_SCORING).map(([k, fn]) => [k, fn(v[k])]));
  const complete = Object.values(perParam).every((x) => x !== null);
  const total = complete ? Object.values(perParam).reduce((a, b) => a + b, 0) : null;
  const anyThree = complete && Object.values(perParam).some((x) => x >= 3);
  let tone = 'good';
  let interp = null;
  if (total !== null) {
    if (total >= 7) { tone = 'danger'; interp = 'Rischio alto — risposta clinica urgente (team emergenza)'; }
    else if (total >= 5) { tone = 'warning'; interp = 'Rischio medio — valutazione clinica urgente'; }
    else if (anyThree) { tone = 'warning'; interp = 'Rischio medio (singolo parametro ≥3) — valutazione'; }
    else if (total >= 1) { tone = 'warning'; interp = 'Rischio basso — monitoraggio 4-6h'; }
    else { interp = 'Nessun rischio — monitoraggio di routine (12h)'; }
  }
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <NumberInput id="news-rr" label="Frequenza respiratoria" unit="atti/min" value={v.rr} onChange={(x) => set('rr', x)} min={0} max={80} />
        <NumberInput id="news-spo2" label="SpO2" unit="%" value={v.spo2} onChange={(x) => set('spo2', x)} min={0} max={100} />
        <div className="space-y-1.5">
          <Label className="text-xs">O2 supplementare</Label>
          <Select value={v.o2} onValueChange={(x) => set('o2', x)}>
            <SelectTrigger data-testid="calc-input-news-o2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="no">No (aria ambiente)</SelectItem>
              <SelectItem value="yes">Sì</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <NumberInput id="news-sbp" label="Pressione sistolica" unit="mmHg" value={v.sbp} onChange={(x) => set('sbp', x)} min={30} max={300} />
        <NumberInput id="news-hr" label="Frequenza cardiaca" unit="bpm" value={v.hr} onChange={(x) => set('hr', x)} min={0} max={250} />
        <div className="space-y-1.5">
          <Label className="text-xs">Coscienza (AVPU)</Label>
          <Select value={v.avpu} onValueChange={(x) => set('avpu', x)}>
            <SelectTrigger data-testid="calc-input-news-avpu"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="A">Alert (vigile)</SelectItem>
              <SelectItem value="V">V - Risponde a stimolo verbale</SelectItem>
              <SelectItem value="P">P - Risponde a stimolo doloroso</SelectItem>
              <SelectItem value="U">U - Non risponde</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <NumberInput id="news-temp" label="Temperatura" unit="°C" value={v.temp} onChange={(x) => set('temp', x)} min={25} max={45} step={0.1} />
      </div>
      <ResultBox value={total} unit={total !== null ? 'punti' : ''} interpretation={interp} tone={tone} />
      <p className="text-[11px] text-muted-foreground">NEWS2 (RCP UK). Non applicabile in gravidanza o BPCO con target SpO2 88-92% (usare scala 2).</p>
    </div>
  );
};
